/* eslint-disable prefer-destructuring */
/* eslint-disable no-unused-vars */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-unstable-nested-components */

'use client'

import { useRouter } from 'next/router'
import { useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { format } from 'date-fns'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  useColorModeValue,
  Flex,
  Avatar,
  Divider,
  SimpleGrid,
} from '@chakra-ui/react'
import { ChevronLeftIcon, CalendarIcon, TimeIcon } from '@chakra-ui/icons'
import { useContentful } from '../../utils/hooks/useContentful'
import { BlogPostSkeleton } from '../../components/blog/BlogPostSkeleton'
import BlogNotFound from '../../components/blog/BlogNotFound'
import { Tags } from '../../components/blog/Tags'

export default function BlogPost() {
  const router = useRouter()
  const { slug } = router.query

  const query = useMemo(() => (slug ? { 'fields.slug': slug, limit: 1, include: 10 } : null), [slug])

  const { data: blog, loading, error } = useContentful('pageBlogPost', query)

  const bgColor = useColorModeValue('gray.900', 'gray.900')
  const textColor = useColorModeValue('gray.100', 'gray.100')
  const mutedTextColor = useColorModeValue('gray.400', 'gray.400')

  const estimatedReadTime = useMemo(() => {
    if (!blog?.fields?.content) return '5 min'
    const blockCount = blog.fields.content.content?.length || 0
    const minutes = Math.max(1, Math.ceil(blockCount * 0.5))
    return `${minutes} min`
  }, [blog])

  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        console.log(node)
        const { image } = node.data.target.fields
        return !image || !image.fields?.file ? null : (
          <Box my={8}>
            <Image
              src={`https:${image.fields.file.url}`}
              alt={image.caption}
              width={800}
              height={600}
              layout="responsive"
              className="rounded-lg"
            />
          </Box>
        )
      },
      [BLOCKS.PARAGRAPH]: (_node, children) => <Text mb={4}>{children}</Text>,
      [BLOCKS.HEADING_1]: (_node, children) => (
        <Heading as="h1" size="2xl" mb={4} mt={8}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_2]: (_node, children) => (
        <Heading as="h2" size="xl" mb={3} mt={6}>
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_3]: (_node, children) => (
        <Heading as="h3" size="lg" mb={2} mt={4}>
          {children}
        </Heading>
      ),
      [BLOCKS.UL_LIST]: (_node, children) => (
        <Box as="ul" styleType="disc" pl={6} mb={4}>
          {children}
        </Box>
      ),
      [BLOCKS.OL_LIST]: (_node, children) => (
        <Box as="ol" styleType="decimal" pl={6} mb={4}>
          {children}
        </Box>
      ),
      [BLOCKS.LIST_ITEM]: (_node, children) => (
        <Box as="li" mb={1}>
          {children}
        </Box>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <Link href={node.data.uri} color="blue.400" _hover={{ textDecoration: 'underline' }}>
          {children}
        </Link>
      ),
    },
    renderMark: {
      [MARKS.BOLD]: text => <Text as="strong">{text}</Text>,
      [MARKS.ITALIC]: text => <Text as="em">{text}</Text>,
      [MARKS.UNDERLINE]: text => <Text as="u">{text}</Text>,
      [MARKS.CODE]: text => (
        <Text as="code" bg="gray.700" px={1} borderRadius="sm">
          {text}
        </Text>
      ),
    },
  }

  if (!router.isReady || loading) {
    return <BlogPostSkeleton />
  }
  if (error) {
    return <Text color="red.500">Error loading blog post: {error.message}</Text>
  }

  if (!blog) {
    return <BlogNotFound />
  }

  const displayDate = blog.fields.publishedDate ? new Date(blog.fields.publishedDate) : new Date(blog.sys.createdAt)

  const author = blog.fields.author

  return (
    <Box bg={bgColor} color={textColor} height="100vh" overflowY="scroll">
      <Container maxW="4xl" py={8}>
        <Link href="/blogs" passHref>
          <Button leftIcon={<ChevronLeftIcon />} variant="ghost" color="blue.400" mb={6} _hover={{ color: 'blue.300' }}>
            Back to all posts
          </Button>
        </Link>

        <VStack as="article" spacing={8} align="stretch">
          <Box position="relative" height="400px" borderRadius="lg" overflow="hidden" boxShadow="xl">
            <Image
              src={`https:${blog.fields.featuredImage.fields.file?.url}`}
              alt={blog.fields.title}
              layout="fill"
              objectFit="cover"
            />
          </Box>

          <VStack spacing={4} align="stretch">
            <Tags categories={blog.fields.categories} />

            <Heading as="h1" size="2xl" color="blue.400">
              {blog.fields.title}
            </Heading>

            <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align="flex-start" gap={4}>
              {author && (
                <HStack spacing={3}>
                  <Avatar
                    size="md"
                    name={author.fields.name}
                    src={author.fields.avatar ? `https:${author.fields.avatar.fields.file?.url}` : undefined}
                  />
                  <Box>
                    <Text fontWeight="medium">{author.fields.name}</Text>
                    {author.fields.bio && (
                      <Text fontSize="sm" color={mutedTextColor} noOfLines={1}>
                        {author.fields.bio}
                      </Text>
                    )}
                  </Box>
                </HStack>
              )}

              <HStack spacing={6} color={mutedTextColor}>
                <Flex align="center">
                  <CalendarIcon mr={2} />
                  <Text>{format(displayDate, 'MMMM d, yyyy')}</Text>
                </Flex>
                <Flex align="center">
                  <TimeIcon mr={2} />
                  <Text>{estimatedReadTime} read</Text>
                </Flex>
              </HStack>
            </Flex>
          </VStack>

          <Divider my={6} borderColor="gray.600" />

          <Box className="blog-content">{documentToReactComponents(blog.fields.content, renderOptions)}</Box>

          {author && author.fields.bio && (
            <Box mt={12} p={6} bg="gray.800" borderRadius="lg">
              <Flex align="start" gap={4}>
                <Avatar
                  size="xl"
                  name={author.fields.name}
                  src={author.fields.avatar ? `https:${author.fields.avatar.fields.file?.url}` : undefined}
                />
                <Box>
                  <Heading as="h3" size="lg" mb={2}>
                    About {author.fields.name}
                  </Heading>
                  <Text>{author.fields.bio}</Text>
                </Box>
              </Flex>
            </Box>
          )}

          {blog.fields.relatedBlogPosts && blog.fields.relatedBlogPosts.length > 0 && (
            <Box mt={12}>
              <Heading as="h2" size="xl" mb={6}>
                Related Articles
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {blog.fields.relatedBlogPosts.map(relatedPost => (
                  <Link key={relatedPost.sys.id} href={`/blogs/${relatedPost.fields.slug}`} passHref>
                    <Flex gap={4} align="start" _hover={{ bg: 'gray.800' }} p={2} borderRadius="md">
                      <Box position="relative" w="100px" h="100px" flexShrink={0}>
                        <Image
                          src={`https:${relatedPost.fields.featuredImage.fields.file?.url}`}
                          alt={relatedPost.fields.title}
                          layout="fill"
                          objectFit="cover"
                          className="rounded-md"
                        />
                      </Box>
                      <Box>
                        <Text fontWeight="medium" color="blue.400" noOfLines={2}>
                          {relatedPost.fields.title}
                        </Text>
                        <Text fontSize="sm" color={mutedTextColor} mt={1} noOfLines={2}>
                          {relatedPost.fields.shortDescription}
                        </Text>
                      </Box>
                    </Flex>
                  </Link>
                ))}
              </SimpleGrid>
            </Box>
          )}

          <Flex justify="center" mt={12}>
            <Link href="/blogs" passHref>
              <Button colorScheme="blue" size="lg">
                Read More Articles
              </Button>
            </Link>
          </Flex>
        </VStack>
      </Container>
    </Box>
  )
}
